import { zodResolver } from '@hookform/resolvers/zod';
import type { SubmitHandler } from 'react-hook-form';
import { useFieldArray, useForm } from 'react-hook-form';
import { BlockButton } from '@/components/BlockButton';
import { TextField } from '@/components/TextField';
import SelectBox from '@/components/SelectBox';
import { EMPLOYMENT_TYPE_ITEMS, STATUS_ITEMS } from '../constant';
import usePostProvidedJobPost from '../api/usePostProvidedJobPost';
import { useDebounce } from '@/hooks/useDebounce';
import type { ChangeEvent } from 'react';
import { useEffect, useRef, useState } from 'react';
import CandidateCompanyList from './CandidateCompanyList';
import type { ProvidedJobPostsFormSchema } from '../schemas/providedJobPostsFormSchema';
import { providedJobPostsFormSchema } from '../schemas/providedJobPostsFormSchema';
import JobContentsDnd from './JobContentsDnd';
import { showErrorToast, showSuccessToast } from '../../job_change_processes/lib/toast';
import { useBeforeUnload } from '../../hooks/useBeforeUnload';
import type { ProvidedJobPostsResponse } from '../types/ProvidedJobPost';
import usePutProvidedJobPost from '../api/usePutProvidedJobPost';
import useFetchSummaryCompanies from '../../api/useFetchSummaryCompanies';
import { useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';

type FormType = 'create' | 'edit';

type InitialData = {
  title?: string;
  jobContentJson?: Array<{
    key: string;
    content?: string;
  }>;
  companyId?: string;
  workingLocation?: string;
  jobPositionName?: string;
  minSalary?: number;
  maxSalary?: number;
};

type Props = {
  formType: FormType;
  providedJobPostData?: ProvidedJobPostsResponse;
  initialData?: InitialData;
};

function getDefaultValues(
  type: FormType,
  providedJobPostData?: ProvidedJobPostsResponse,
  initialData?: InitialData
) {
  switch (type) {
    case 'create': {
      // initialDataがある場合はそれを使用、なければデフォルト値
      if (initialData) {
        return {
          title: initialData.title || '',
          status: 'published',
          companyId: initialData.companyId || '',
          companyName: '',
          jobPositionName: initialData.jobPositionName || '',
          workingLocation: initialData.workingLocation || '',
          employmentType: '' as const,
          minSalary: initialData.minSalary != null ? String(initialData.minSalary) : '',
          maxSalary: initialData.maxSalary != null ? String(initialData.maxSalary) : '',
          jobContentJson:
            initialData.jobContentJson && initialData.jobContentJson.length > 0
              ? initialData.jobContentJson
              : [
                  {
                    key: '仕事内容',
                    content: '',
                  },
                  {
                    key: '勤務地',
                    content: '',
                  },
                  {
                    key: '必須要件',
                    content: '',
                  },
                  {
                    key: '歓迎要件',
                    content: '',
                  },
                  {
                    key: '給与',
                    content: '',
                  },
                  {
                    key: '就業環境',
                    content: '',
                  },
                ],
        };
      }
      return {
        title: '',
        status: 'published',
        companyId: '',
        companyName: '',
        jobPositionName: '',
        workingLocation: '',
        employmentType: '' as const,
        minSalary: '',
        maxSalary: '',
        jobContentJson: [
          {
            key: '仕事内容',
            content: '',
          },
          {
            key: '勤務地',
            content: '',
          },
          {
            key: '必須要件',
            content: '',
          },
          {
            key: '歓迎要件',
            content: '',
          },
          {
            key: '給与',
            content: '',
          },
          {
            key: '就業環境',
            content: '',
          },
        ],
      };
    }

    case 'edit': {
      return {
        title: providedJobPostData?.title ?? '',
        status: providedJobPostData?.status ?? 'published',
        companyId: providedJobPostData?.company.id ? String(providedJobPostData?.company.id) : '',
        jobPositionName: providedJobPostData?.job_position_name ?? '',
        workingLocation: providedJobPostData?.working_location ?? '',
        employmentType: providedJobPostData?.employment_type ?? ('' as const),
        minSalary: providedJobPostData?.min_salary ? String(providedJobPostData?.min_salary) : '',
        maxSalary: providedJobPostData?.max_salary ? String(providedJobPostData?.max_salary) : '',
        jobContentJson: providedJobPostData?.job_content_json,
      };
    }
  }
}

export default function ProvidedJobPostsForm({
  formType,
  providedJobPostData,
  initialData,
}: Props) {
  const [companyName, setCompanyName] = useState('');
  const [debouncedCompanyName, setDebouncedCompanyName] = useState('');
  const [isCompanyFocus, setIsCompanyFocus] = useState(false);
  const companyListRef = useRef<HTMLDivElement>(null);
  const { mutateAsync: postMutateAsync } = usePostProvidedJobPost();
  const { mutateAsync: putMutateAsync } = usePutProvidedJobPost(providedJobPostData?.id ?? 0);
  const {
    data: summaryCompaniesData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useFetchSummaryCompanies(debouncedCompanyName);
  const debounce = useDebounce(500);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    register,
    handleSubmit,
    formState: { isSubmitting, isValid, isDirty },
    setValue,
    watch,
    control,
    trigger,
  } = useForm<ProvidedJobPostsFormSchema>({
    resolver: zodResolver(providedJobPostsFormSchema),
    mode: 'all',
    defaultValues: getDefaultValues(formType, providedJobPostData, initialData),
  });
  useBeforeUnload(isDirty && !isSubmitting);

  // initialDataでプリフィルされた場合、初回マウント時にバリデーションを実行して保存ボタンを有効化する
  useEffect(() => {
    if (formType === 'create' && initialData) {
      trigger();
    }
  }, [formType, initialData, trigger]);

  const { fields, append, remove, move } = useFieldArray({
    name: 'jobContentJson',
    control,
  });

  const jobContentJson = watch('jobContentJson');

  const onSubmit: SubmitHandler<ProvidedJobPostsFormSchema> = async (data) => {
    if (formType === 'create') {
      try {
        const res = await postMutateAsync({
          title: data.title,
          status: data.status,
          // 既存企業選択時は company_id、フリーテキスト時は company_name を送信
          company_id: data.companyId ? Number(data.companyId) : undefined,
          company_name: data.companyId ? undefined : data.companyName?.trim(),
          job_position_name: data.jobPositionName,
          working_location: data.workingLocation,
          employment_type: data.employmentType || null,
          min_salary: data.minSalary ? Number(data.minSalary) : undefined,
          max_salary: data.maxSalary ? Number(data.maxSalary) : undefined,
          job_content_json: data.jobContentJson,
        });
        // キャッシュを無効化して最新のデータを取得
        await queryClient.invalidateQueries({ queryKey: ['providedJobPost', res.id] });
        await queryClient.invalidateQueries({ queryKey: ['providedJobPosts'] });
        // 最新データの取得を待ってから遷移
        await queryClient.refetchQueries({ queryKey: ['providedJobPost', res.id] });
        showSuccessToast('求人票を作成しました');
        navigate({
          to: '/agent/provided_job_posts/$providedJobPostsId',
          params: { providedJobPostsId: res.id.toString() },
        });
      } catch (error) {
        showErrorToast('求人票作成に失敗しました');
      }
    } else {
      try {
        const res = await putMutateAsync({
          title: data.title,
          status: data.status,
          company_id: Number(data.companyId),
          job_position_name: data.jobPositionName,
          working_location: data.workingLocation,
          employment_type: data.employmentType || null,
          min_salary: data.minSalary ? Number(data.minSalary) : undefined,
          max_salary: data.maxSalary ? Number(data.maxSalary) : undefined,
          job_content_json: data.jobContentJson,
        });
        // キャッシュを無効化して最新のデータを取得
        await queryClient.invalidateQueries({ queryKey: ['providedJobPost', res.id] });
        await queryClient.invalidateQueries({ queryKey: ['providedJobPosts'] });
        // 最新データの取得を待ってから遷移
        await queryClient.refetchQueries({ queryKey: ['providedJobPost', res.id] });
        showSuccessToast('求人票を更新しました');
        navigate({
          to: '/agent/provided_job_posts/$providedJobPostsId',
          params: { providedJobPostsId: res.id.toString() },
        });
      } catch (error) {
        showErrorToast('求人票更新に失敗しました');
      }
    }
  };

  const onInputCompany = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.currentTarget.value;
    setCompanyName(value);

    // create フローのみ companyId をクリアして companyName を同期する。
    // edit フローでは companyName を送信しないため、ここで companyId をクリアすると
    // PUT 時に Number('') = 0 が送られ 422 になってしまうので維持する。
    if (formType === 'create') {
      setValue('companyId', '', { shouldValidate: false });
      setValue('companyName', value, { shouldValidate: true });
    }

    debounce(() => {
      setDebouncedCompanyName(value);
    });
  };

  const handleCompanyListScroll = () => {
    const element = companyListRef.current;
    if (!element) return;
    if (element.scrollHeight - element.scrollTop - element.clientHeight < 50) {
      if (hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    }
  };

  const onSelectCompany = (id: number, name: string) => {
    // 既存企業を選択した場合: companyId を設定して companyName はクリア
    setValue('companyId', String(id), { shouldValidate: true });
    setValue('companyName', '', { shouldValidate: true });
    setCompanyName(name);
  };

  const onCreateNewCompany = (name: string) => {
    // 新規作成を選択した場合: companyName を設定して companyId はクリア
    setValue('companyId', '', { shouldValidate: false });
    setValue('companyName', name, { shouldValidate: true });
    setCompanyName(name);
    setIsCompanyFocus(false);
  };

  const filteredCompanies = summaryCompaniesData?.pages?.flatMap((page) => page.records) ?? [];

  useEffect(() => {
    if (formType === 'edit') {
      setCompanyName(providedJobPostData?.company.name ?? '');
    } else if (formType === 'create' && initialData?.companyId && summaryCompaniesData?.pages) {
      // initialDataにcompanyIdがある場合、企業名を取得して設定
      const allRecords = summaryCompaniesData.pages.flatMap((page) => page.records);
      const company = allRecords.find(
        (c: { id: number; name: string }) => c.id === Number(initialData.companyId)
      );
      if (company) {
        setCompanyName(company.name);
      }
    }
  }, [formType, initialData, summaryCompaniesData]);

  return (
    <form
      className="relative flex max-w-[1190px] flex-col gap-4"
      action=""
      onSubmit={handleSubmit(onSubmit)}
    >
      <div className="flex gap-4">
        <div className="flex w-full flex-1 flex-col gap-2">
          <div className="flex items-center gap-2">
            <p className="text-sm font-bold">タイトル</p>
            <span className="rounded-full bg-danger_lighten px-3 py-1 text-xs font-bold text-danger">
              必須
            </span>
          </div>
          <div className="flex">
            <TextField {...register('title')} placeholder="求人票のタイトルを入力" />
          </div>
        </div>
        {!providedJobPostData?.outer_job_post ? (
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <p className="text-sm font-bold">ステータス</p>
              <span className="rounded-full bg-danger_lighten px-3 py-1 text-xs font-bold text-danger">
                必須
              </span>
            </div>
            <div className="flex w-[150px]">
              <SelectBox {...register('status')} options={STATUS_ITEMS} />
            </div>
          </div>
        ) : null}
      </div>
      <div className="flex gap-4">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <p className="text-sm font-bold">企業名</p>
            <span className="rounded-full bg-danger_lighten px-3 py-1 text-xs font-bold text-danger">
              必須
            </span>
          </div>
          <div className="relative flex w-[442px]">
            <TextField
              placeholder="企業名を入力"
              onChange={onInputCompany}
              value={companyName}
              onFocus={() => setIsCompanyFocus(true)}
              onBlur={() => setTimeout(() => setIsCompanyFocus(false), 200)}
            />
            {isCompanyFocus && (
              <div className="absolute top-11 z-50">
                <CandidateCompanyList
                  ref={companyListRef}
                  companies={filteredCompanies}
                  onSelectCompany={onSelectCompany}
                  onScroll={handleCompanyListScroll}
                  isLoadingMore={isFetchingNextPage}
                  inputText={formType === 'create' ? companyName : undefined}
                  onCreateNew={formType === 'create' ? onCreateNewCompany : undefined}
                />
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <p className="text-sm font-bold">ポジション</p>
            <span className="rounded-full bg-danger_lighten px-3 py-1 text-xs font-bold text-danger">
              必須
            </span>
          </div>
          <div className="flex w-[303px]">
            <TextField {...register('jobPositionName')} placeholder="営業" />
          </div>
        </div>
      </div>
      <div className="flex gap-4">
        <div className="flex flex-col gap-2">
          <p className="text-sm font-bold">勤務地</p>
          <div className="flex w-[226px]">
            <TextField {...register('workingLocation')} placeholder="東京都渋谷区" />
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <p className="text-sm font-bold">年収例</p>
          <div className="flex items-center gap-2">
            <div className="flex w-[129px]">
              <TextField
                {...register('minSalary')}
                placeholder="600"
                onInput={(e) =>
                  (e.currentTarget.value = e.currentTarget.value.replace(/[^0-9]/g, ''))
                }
              />
            </div>
            <span className="text-base">〜</span>
            <div className="flex w-[129px]">
              <TextField
                {...register('maxSalary')}
                placeholder="600"
                onInput={(e) =>
                  (e.currentTarget.value = e.currentTarget.value.replace(/[^0-9]/g, ''))
                }
              />
            </div>
            <span className="text-base">万円</span>
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <p className="text-sm font-bold">雇用形態</p>
          <div className="flex w-[180px]">
            <SelectBox {...register('employmentType')} options={EMPLOYMENT_TYPE_ITEMS} />
          </div>
        </div>
      </div>
      <div className="mt-4 flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <p className="text-sm font-bold">求人情報</p>
          <span className="rounded-full bg-danger_lighten px-3 py-1 text-xs font-bold text-danger">
            必須
          </span>
        </div>
        <JobContentsDnd
          remove={remove}
          fields={fields}
          jobContentJson={jobContentJson}
          register={register}
          move={move}
        />
        <div className="w-[128px]">
          <BlockButton
            type="button"
            icon="/agent/provided_job_posts/icon-add.svg"
            iconSize={20}
            height={52}
            text="新規追加"
            colorType="white"
            onClick={(e) => {
              e.stopPropagation();
              append({ key: '', content: '' });
            }}
          />
        </div>
      </div>
      <div className="sticky right-0 bottom-6 mt-auto ml-auto w-[96px]">
        <BlockButton
          type="submit"
          text="保存する"
          height={52}
          disabled={isSubmitting || !isValid}
        />
      </div>
    </form>
  );
}
